import apiClient from '../../../api/axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TProvidersResponse,
  TProviderResponse,
  TCreateProviderRequest,
  TRenameProviderRequest,
  TUpsertGlobalFieldsRequest,
  TToggleEnabledRequest,
  TAddFieldRequest,
  TUpdateFieldRequest,
  TRestaurantConfigsResponse,
  TUpsertClientFieldsRequest,
} from '../schemas/payment-provider-schema';

const PROVIDERS_BASE_URL = '/kiosk-pos-admin/payment-terminal-providers';
const RESTAURANT_CONFIGS_BASE_URL = '/kiosk-pos-admin/restaurant-configs';

const PROVIDERS_QUERY_KEY = 'payment-terminal-providers';
const RESTAURANT_CONFIGS_QUERY_KEY = 'payment-terminal-restaurant-configs';

export const useGetPaymentProviders = () => {
  return useQuery({
    queryKey: [PROVIDERS_QUERY_KEY],
    queryFn: async () => {
      const response = await apiClient.get<TProvidersResponse>(PROVIDERS_BASE_URL);
      return response.data?.data?.rows || [];
    },
  });
};

export const useGetPaymentProvider = (providerId: string) => {
  return useQuery({
    queryKey: [PROVIDERS_QUERY_KEY, providerId],
    queryFn: async () => {
      const response = await apiClient.get<TProviderResponse>(`${PROVIDERS_BASE_URL}/${providerId}`);
      return response.data?.data;
    },
    enabled: !!providerId,
  });
};

export const useCreatePaymentProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: TCreateProviderRequest) => {
      const response = await apiClient.post<TProviderResponse>(PROVIDERS_BASE_URL, payload);
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_QUERY_KEY] });
    },
  });
};

export const useRenameProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, payload }: { providerId: string; payload: TRenameProviderRequest }) => {
      const response = await apiClient.patch<TProviderResponse>(`${PROVIDERS_BASE_URL}/${providerId}`, payload);
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_QUERY_KEY] });
    },
  });
};

export const useUpsertProviderGlobalFields = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, payload }: { providerId: string; payload: TUpsertGlobalFieldsRequest }) => {
      const response = await apiClient.put<TProviderResponse>(`${PROVIDERS_BASE_URL}/${providerId}`, payload);
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_QUERY_KEY] });
    },
  });
};

export const useToggleProviderEnabled = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, payload }: { providerId: string; payload: TToggleEnabledRequest }) => {
      const response = await apiClient.patch<TProviderResponse>(
        `${PROVIDERS_BASE_URL}/${providerId}/enabled`,
        payload,
      );
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_QUERY_KEY] });
    },
  });
};

export const useDeleteProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (providerId: string) => {
      const response = await apiClient.delete(`${PROVIDERS_BASE_URL}/${providerId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_QUERY_KEY] });
    },
  });
};

export const useAddProviderField = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, payload }: { providerId: string; payload: TAddFieldRequest }) => {
      const response = await apiClient.post<TProviderResponse>(
        `${PROVIDERS_BASE_URL}/${providerId}/fields`,
        payload,
      );
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_QUERY_KEY] });
    },
  });
};

export const useUpdateProviderField = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      providerId,
      fieldId,
      payload,
    }: {
      providerId: string;
      fieldId: string;
      payload: TUpdateFieldRequest;
    }) => {
      const response = await apiClient.patch<TProviderResponse>(
        `${PROVIDERS_BASE_URL}/${providerId}/fields/${fieldId}`,
        payload,
      );
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_QUERY_KEY] });
    },
  });
};

export const useDeleteProviderField = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, fieldId }: { providerId: string; fieldId: string }) => {
      const response = await apiClient.delete(`${PROVIDERS_BASE_URL}/${providerId}/fields/${fieldId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_QUERY_KEY] });
    },
  });
};

export const useGetRestaurantPaymentConfigs = (restaurantId: string) => {
  return useQuery({
    queryKey: [RESTAURANT_CONFIGS_QUERY_KEY, restaurantId],
    queryFn: async () => {
      const response = await apiClient.get<TRestaurantConfigsResponse>(
        `${RESTAURANT_CONFIGS_BASE_URL}/${restaurantId}`,
      );
      return response.data?.data;
    },
    enabled: !!restaurantId,
  });
};

export const useUpsertRestaurantConfig = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, payload }: { providerId: string; payload: TUpsertClientFieldsRequest }) => {
      const response = await apiClient.put(
        `${RESTAURANT_CONFIGS_BASE_URL}/${restaurantId}/${providerId}`,
        payload,
      );
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RESTAURANT_CONFIGS_QUERY_KEY, restaurantId] });
    },
  });
};

export const useDeleteRestaurantConfig = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (providerId: string) => {
      const response = await apiClient.delete(
        `${RESTAURANT_CONFIGS_BASE_URL}/${restaurantId}/${providerId}`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RESTAURANT_CONFIGS_QUERY_KEY, restaurantId] });
    },
  });
};

export const useSetDefaultRestaurantConfig = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (providerId: string) => {
      const response = await apiClient.patch(
        `${RESTAURANT_CONFIGS_BASE_URL}/${restaurantId}/${providerId}/default`,
        {},
      );
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RESTAURANT_CONFIGS_QUERY_KEY, restaurantId] });
    },
  });
};
